/**
 * State that the whole signed-in app needs, loaded once in `AppShell` and
 * handed down through the router outlet.
 *
 * It lives in one place because three separate screens were each loading the
 * daily prompt for themselves, and because the feed gate has to agree with the
 * composer about whether you have posted. Two components asking Firestore the
 * same question independently is how they end up disagreeing.
 */
import { useOutletContext } from 'react-router-dom';
import type { AiGenerationStatus } from '../../shared/schemas';

export interface DailyPrompt {
  category: string;
  output: string;
  aiPun: string;
  aiStatus: AiGenerationStatus | null;
}

export interface AppState {
  userId: string;
  prompt: DailyPrompt | null;
  /** The prompt document is missing or unreadable. */
  promptFailed: boolean;
  /** What you posted today, or null if you have not posted yet. */
  submittedPun: string | null;
  /** Still checking. Screens should wait rather than flash the wrong state. */
  checkingSubmission: boolean;
  /**
   * The check itself failed, so we do not know either way.
   *
   * Distinct from `submittedPun === null`, which is a real answer. Collapsing
   * the two is what turned a single failed read into "you have not posted",
   * which locks the feed for the rest of the session with nothing but a console
   * line to say why.
   */
  submissionCheckFailed: boolean;
  /** Run the check again after a failure. */
  recheckSubmission: () => void;
  /** Called after a successful post so the feed gate opens without a refetch. */
  onSubmitted: (text: string) => void;
  crownCount: number;
  /** Until the prompt rotates at Pacific midnight. */
  secondsLeft: number;
}

export function useAppState(): AppState {
  return useOutletContext<AppState>();
}
